import { useEffect, useState, type ReactNode } from 'react'
import { Box, Button, Card, Segmented, Stack, Text } from '@grovus/design-system'
import { AuthLayout } from '../auth/AuthLayout'
import { fetchSession } from '../auth/api'
import { reportGuestChanged } from '../auth/session'
import { openShareLink } from '../data/shareLinks'
import { LANG_OPTIONS, SHARE_COPY, type Dict, type Lang } from '../i18n'
import type { Theme } from '../lib/preferences'
import { clearGuestMarker, writeGuestMarker } from './guestMarker'
import { captureShareToken, clearShareToken, heldShareToken } from './shareToken'

interface Chrome {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
}

/** The centered panel with the language and theme controls, shared by both pages here. */
function SharePanel({ t, lang, setLang, theme, toggleTheme, board, children }: Chrome & { board: string; children: ReactNode }) {
  return (
    <AuthLayout
      maxWidth={520}
      topRight={(
        <>
          <Segmented value={lang} onChange={(v) => setLang(v as Lang)} data={LANG_OPTIONS} />
          <Button variant="default" onClick={toggleTheme}>{theme === 'light' ? t.dark : t.light}</Button>
        </>
      )}
    >
      <Box data-testid="share-link" data-board={board}>
        <Card>{children}</Card>
      </Box>
    </AuthLayout>
  )
}

type Board = 'opening' | 'ended' | 'failed' | 'signedIn'

/**
 * `/share#<token>` (GRO-83 design, "The `/share` page"). The token leaves the URL first;
 * a member session skips the open; otherwise the token is posted to `/open`. On success
 * the guest marker is written from the answer, the other tabs hear `guest-changed`, and
 * the boundary decides again from `/`.
 */
export function ShareLinkPage({ onOpened, onLeave, ...chrome }: Chrome & {
  /** The link opened: the boundary runs entry again, which finds the guest session. */
  onOpened: () => void
  /** "Go to Grovus" from the signed-in message. */
  onLeave: () => void
}) {
  const copy = SHARE_COPY[chrome.lang]
  const [board, setBoard] = useState<Board>('opening')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let current = true
    async function run() {
      captureShareToken()
      setBoard('opening')
      if (attempt === 0) {
        const session = await fetchSession()
        if (!current) return
        if (session.status === 'authenticated') {
          clearShareToken()
          setBoard('signedIn')
          return
        }
      }
      const token = heldShareToken()
      if (token === null) {
        setBoard('ended')
        return
      }
      const result = await openShareLink(token)
      if (!current) return
      if (result.status === 'ok') {
        clearShareToken()
        writeGuestMarker(result.guest)
        reportGuestChanged()
        window.history.replaceState(null, '', '/')
        onOpened()
      } else if (result.status === 'ended') {
        clearShareToken()
        setBoard('ended')
      } else {
        setBoard('failed')
      }
    }
    void run()
    return () => { current = false }
  }, [attempt, onOpened])

  let body: ReactNode
  if (board === 'opening') body = <Text fz="sm" role="status">{copy.opening}</Text>
  else if (board === 'ended') body = <Text role="alert">{copy.ended}</Text>
  else if (board === 'failed') {
    body = (
      <Stack gap={12}>
        <Text role="alert">{copy.openFailed}</Text>
        <Box><Button onClick={() => setAttempt((n) => n + 1)}>{copy.retry}</Button></Box>
      </Stack>
    )
  } else {
    body = (
      <Stack gap={12}>
        <Text>{copy.signedIn}</Text>
        <Box><Button onClick={onLeave}>{copy.goToGrovus}</Button></Box>
      </Stack>
    )
  }
  return <SharePanel {...chrome} board={board}>{body}</SharePanel>
}

/**
 * The ended-guest page (GRO-83 design, "The guest marker"): this browser held a guest grant
 * that no longer works. "Sign in" clears the marker, so the next visit is an ordinary one.
 */
export function GuestEndedPage({ onSignIn, ...chrome }: Chrome & { onSignIn: () => void }) {
  const copy = SHARE_COPY[chrome.lang]
  return (
    <SharePanel {...chrome} board="guestEnded">
      <Stack gap={12}>
        <Text role="alert">{copy.ended}</Text>
        <Box>
          <Button variant="default" onClick={() => {
            clearGuestMarker()
            onSignIn()
          }}>{copy.signIn}</Button>
        </Box>
      </Stack>
    </SharePanel>
  )
}
